/**
 * Encodage et VALIDATION des messages du reseau.
 *
 * Le meme code sert des deux cotes, et c'est le point: le serveur ne croit pas
 * le client, mais le client ne croit pas davantage le serveur. Un message mal
 * forme est ecarte ici, une fois, au lieu de faire tomber une boucle de rendu
 * trois modules plus loin.
 *
 * Tout ce qui est refuse renvoie `null`. Aucune exception ne remonte: une trame
 * abimee ne doit jamais couper une partie, exactement comme une entree abimee de
 * `data/monde.json` ne doit jamais empecher le monde de se charger.
 */
import type { EtatJoueur } from '../types/player.ts'
import type { Batiment, Vehicule } from '../types/monde.ts'
import type {
  EtatPresence,
  EvenementMonde,
  JoueurConnecte,
  MessageChat,
  MessageClient,
  MessageServeur,
} from '../types/reseau.ts'
import { CARTE_X, CARTE_Z } from '../constants.ts'
import { validerApparence } from '../data/apparence.ts'
import { BATIMENTS } from '../data/batiments.ts'
import { VEHICULES } from '../data/vehicules.ts'
import { ANIMAUX } from '../data/animaux.ts'
import { NIVEAU_MAX } from './batiments.ts'
import { CHAMPS_FAUNE, MAX_FAUNE } from './faune-reseau.ts'
import { CHAMPS_ENTREE, MAX_ENTREES_LOT, decoderEntrees } from './entree.ts'

/**
 * Etats de joueur acceptes, en table plutot qu'en liste: le compilateur exige
 * alors une entree par valeur de `EtatJoueur`, donc ajouter un etat sans le
 * declarer ici ne compile pas. Une liste, elle, aurait silencieusement laisse
 * les autres joueurs figes dans leur pose precedente.
 */
const ETATS: Record<EtatJoueur, true> = {
  immobile: true,
  marche: true,
  course: true,
  recolte: true,
  mange: true,
  dort: true,
  construit: true,
}

/** Longueur maximale d'un identifiant recu. Large, mais pas illimitee. */
const MAX_ID = 64

// ------------------------------------------------------------------- chat

/** Longueur maximale d'une ligne de conversation, en caracteres affiches. */
export const MAX_TEXTE_CHAT = 200

/**
 * Au-dela de cette taille, on ne nettoie meme pas: on jette.
 *
 * Nettoyer, c'est passer une expression reguliere sur la chaine. Le faire sur
 * un megaoctet envoye expres serait offrir au premier client modifie venu un
 * moyen d'occuper le serveur pour le prix d'un `send`.
 */
const MAX_BRUT_CHAT = 1000

/** Nombre de lignes que le serveur garde pour celui qui arrive. */
export const HISTORIQUE_CHAT = 30

/**
 * Debit maximal: quatre lignes par tranche de deux secondes, soit deux par
 * seconde en regime etabli. Assez pour une conversation vive, trop peu pour
 * noyer l'ecran des autres depuis une console de navigateur.
 */
export const CHAT_MAX_PAR_FENETRE = 4
export const CHAT_FENETRE_MS = 2000

/**
 * Ce qu'on retire d'un texte recu.
 *
 * Les caracteres de controle d'abord (un `\n` casserait une ligne du journal en
 * deux, et le fichier de session doit rester relisible ligne par ligne), mais
 * aussi les marques de direction et les espaces de largeur nulle: elles ne se
 * voient pas et permettent de retourner l'affichage du voisin ou de fabriquer
 * un message qui parait vide.
 */
const CARACTERES_INVISIBLES =
  /[\u0000-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u2028\u2029\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g

/**
 * Rend le texte tel qu'il sera affiche, ou `null` s'il n'y a rien a dire.
 *
 * La MEME fonction sert des deux cotes: le navigateur s'en sert pour ne pas
 * envoyer une ligne vide, le serveur pour ne rien croire de ce qu'il recoit.
 * Deux regles pour la meme question finiraient par diverger.
 */
export function nettoyerTexteChat(v: unknown): string | null {
  if (typeof v !== 'string' || v.length > MAX_BRUT_CHAT) return null
  const propre = v.replace(CARACTERES_INVISIBLES, ' ').replace(/\s+/g, ' ').trim()
  if (propre.length === 0) return null
  // On coupe en POINTS DE CODE, pas en unites UTF-16: trancher `.slice()` au
  // milieu d'un emoji laisse un demi-caractere que rien n'affiche.
  const lettres = [...propre]
  return lettres.length > MAX_TEXTE_CHAT ? lettres.slice(0, MAX_TEXTE_CHAT).join('') : propre
}

/**
 * Dit si une ligne de plus est acceptable, et note l'instant si elle l'est.
 *
 * `instants` est l'etat de debit d'UNE connexion, modifie sur place; l'heure
 * arrive en argument parce que `shared/` ne lit jamais l'horloge lui-meme.
 */
export function chatDebitOk(instants: number[], maintenant: number): boolean {
  while (instants.length > 0 && maintenant - (instants[0] ?? 0) > CHAT_FENETRE_MS) instants.shift()
  if (instants.length >= CHAT_MAX_PAR_FENETRE) return false
  instants.push(maintenant)
  return true
}

/**
 * Fabrique la ligne qui sera diffusee.
 *
 * L'IDENTITE VIENT DE LA CONNEXION, le texte seul vient du client. C'est la
 * regle entiere du chat, et elle tient ici, a un seul endroit, pour qu'aucun
 * appelant n'ait la possibilite de recopier un nom recu sur le fil.
 */
export function composerChat(
  n: number,
  qui: { readonly nom: string; readonly compte: string },
  texte: unknown,
): MessageChat | null {
  const propre = nettoyerTexteChat(texte)
  return propre === null ? null : { n, nom: qui.nom, compte: qui.compte, texte: propre }
}

export function encoder(m: MessageClient | MessageServeur): string {
  return JSON.stringify(m)
}

function objet(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

function nombre(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/**
 * Une coordonnee de la CARTE. Hors bornes = message abime, pas joueur voyageur.
 *
 * La borne etait ecrite sur `WORLD_SIZE`, donc sur l'ile 1: un joueur du monde
 * 2, a x = 1300, aurait vu chacune de ses trames rejetee comme corrompue. C'est
 * pour ce genre de changement — meme structure sur le fil, autre contrat — que
 * `VERSION_PROTOCOLE` monte d'un cran.
 */
function coordonnee(v: unknown): number | null {
  const n = nombre(v)
  if (n === null) return null
  const marge = Math.max(CARTE_X, CARTE_Z)
  return n >= -marge && n <= CARTE_X + marge ? n : null
}

function texte(v: unknown, max = MAX_ID): string | null {
  return typeof v === 'string' && v.length <= max ? v : null
}

/** Identifiant optionnel: une chaine courte, ou null. Jamais autre chose. */
function idOuNull(v: unknown): string | null | undefined {
  if (v === null) return null
  if (typeof v === 'string' && v.length <= MAX_ID) return v
  return undefined
}

export function validerEtatPresence(v: unknown): EtatPresence | null {
  const o = objet(v)
  if (!o) return null
  const x = coordonnee(o['x'])
  const z = coordonnee(o['z'])
  const rot = nombre(o['rot'])
  const etat = texte(o['etat'], 16)
  if (x === null || z === null || rot === null || etat === null) return null
  if (!Object.hasOwn(ETATS, etat)) return null
  if (typeof o['nage'] !== 'boolean') return null
  const vehicule = idOuNull(o['vehicule'])
  const interieur = idOuNull(o['interieur'])
  if (vehicule === undefined || interieur === undefined) return null
  // L'appat est facultatif: un client d'une version anterieure n'en envoie pas,
  // et cela ne doit pas le rendre invisible aux autres.
  const appat = o['appat'] === undefined ? null : idOuNull(o['appat'])
  if (appat === undefined) return null
  // L'etage est facultatif et BORNE, comme le champ `niv` des entrees: il ne
  // sert qu'a dessiner le voisin a la bonne hauteur, un nombre absurde ferait
  // flotter un bonhomme a mille metres.
  const brut = nombre(o['niveau'])
  const niveau = brut === null ? 0 : Math.min(NIVEAU_MAX, Math.max(0, Math.floor(brut)))
  return { x, z, rot, etat: etat as EtatJoueur, nage: o['nage'], vehicule, interieur, niveau, appat }
}

function validerJoueurConnecte(v: unknown): JoueurConnecte | null {
  const o = objet(v)
  if (!o) return null
  const id = texte(o['id'])
  const nom = texte(o['nom'], 48)
  const compte = texte(o['compte'], 48)
  const etat = validerEtatPresence(o['etat'])
  if (id === null || nom === null || compte === null || etat === null) return null
  // `validerApparence` remplace ce qu'elle ne connait pas par une valeur sure:
  // un catalogue d'habits qui a change ne doit pas rendre un joueur invisible.
  return { id, nom, compte, apparence: validerApparence(o['apparence']), etat }
}

function validerBatiment(v: unknown): Batiment | null {
  const o = objet(v)
  if (!o) return null
  const id = texte(o['id'])
  const kind = texte(o['kind'], 32)
  const pos = objet(o['pos'])
  if (id === null || kind === null || !pos) return null
  if (!Object.hasOwn(BATIMENTS, kind)) return null
  const x = coordonnee(pos['x'])
  const z = coordonnee(pos['z'])
  const y = nombre(o['y'])
  const rot = nombre(o['rot'])
  const proprietaire = texte(o['proprietaire'], 64)
  const couleurs = objet(o['couleurs'])
  if (x === null || z === null || y === null || rot === null || proprietaire === null || !couleurs) return null
  const murs = nombre(couleurs['murs'])
  const toit = nombre(couleurs['toit'])
  const porte = nombre(couleurs['porte'])
  if (murs === null || toit === null || porte === null) return null
  return {
    id,
    kind: kind as Batiment['kind'],
    pos: { x, z },
    y,
    rot: ((Math.round(rot) % 4) + 4) % 4,
    proprietaire,
    couleurs: { murs, toit, porte },
  }
}

function validerVehiculeRecu(v: unknown): Vehicule | null {
  const o = objet(v)
  if (!o) return null
  const id = texte(o['id'])
  const kind = texte(o['kind'], 32)
  const pos = objet(o['pos'])
  if (id === null || kind === null || !pos) return null
  if (!Object.hasOwn(VEHICULES, kind)) return null
  const x = coordonnee(pos['x'])
  const z = coordonnee(pos['z'])
  const y = nombre(o['y'])
  const rot = nombre(o['rot'])
  const proprietaire = texte(o['proprietaire'], 64)
  const couleur = nombre(o['couleur'])
  if (x === null || z === null || y === null || rot === null || proprietaire === null || couleur === null) {
    return null
  }
  return {
    id,
    kind: kind as Vehicule['kind'],
    pos: { x, z },
    y,
    rot,
    proprietaire,
    couleur: Math.round(couleur),
    ancre: o['ancre'] === true,
    verrouille: o['verrouille'] === true,
    ...(typeof o['retour'] === 'string' && o['retour'].length > 0 && o['retour'].length <= 64 ? { retour: o['retour'] } : {}),
  }
}

function validerEvenement(v: unknown): EvenementMonde | null {
  const o = objet(v)
  if (!o) return null
  switch (o['quoi']) {
    case 'batiment-pose': {
      const b = validerBatiment(o['batiment'])
      return b ? { quoi: 'batiment-pose', batiment: b } : null
    }
    case 'batiment-demoli': {
      const id = texte(o['id'])
      return id === null ? null : { quoi: 'batiment-demoli', id }
    }
    case 'vehicule-pose': {
      const veh = validerVehiculeRecu(o['vehicule'])
      return veh ? { quoi: 'vehicule-pose', vehicule: veh } : null
    }
    case 'vehicule-retire': {
      const id = texte(o['id'])
      return id === null ? null : { quoi: 'vehicule-retire', id }
    }
    case 'ressource': {
      const ressourceId = nombre(o['ressourceId'])
      const pv = nombre(o['pv'])
      const repousseA = nombre(o['repousseA'])
      if (ressourceId === null || pv === null || repousseA === null) return null
      return { quoi: 'ressource', ressourceId: Math.round(ressourceId), pv: Math.round(pv), repousseA }
    }
    case 'jardin': {
      const j = objet(o['jardin'])
      if (!j) return null
      const batimentId = texte(j['batimentId'])
      const repousseA = nombre(j['repousseA'])
      if (batimentId === null || batimentId.length === 0 || repousseA === null) return null
      return { quoi: 'jardin', jardin: { batimentId, repousseA } }
    }
    case 'animal': {
      const a = objet(o['animal'])
      if (!a) return null
      const animalId = nombre(a['animalId'])
      const kind = texte(a['kind'], 32)
      const pos = objet(a['pos'])
      if (animalId === null || kind === null || !pos) return null
      if (!Object.hasOwn(ANIMAUX, kind)) return null
      const x = coordonnee(pos['x'])
      const z = coordonnee(pos['z'])
      if (x === null || z === null) return null
      const ancre = objet(a['ancre'])
      const ax = ancre ? coordonnee(ancre['x']) : null
      const az = ancre ? coordonnee(ancre['z']) : null
      return {
        quoi: 'animal',
        animal: {
          animalId: Math.round(animalId),
          kind,
          repas: nombre(a['repas']) ?? 0,
          dernierRepas: nombre(a['dernierRepas']) ?? 0,
          ami: texte(a['ami'], 64) ?? '',
          maison: texte(a['maison'], 64) ?? '',
          pos: { x, z },
          ancre: ax !== null && az !== null ? { x: ax, z: az } : null,
          rayon: nombre(a['rayon']) ?? 0,
        },
      }
    }
    case 'animal-libere': {
      const animalId = nombre(o['animalId'])
      return animalId === null ? null : { quoi: 'animal-libere', animalId: Math.round(animalId) }
    }
    default:
      return null
  }
}

/** Decode ce qu'un CLIENT envoie. Renvoie null si le message est douteux. */
export function decoderClient(brut: string): MessageClient | null {
  let v: unknown
  try {
    v = JSON.parse(brut)
  } catch {
    return null
  }
  const o = objet(v)
  if (!o) return null
  if (o['t'] === 'bonjour') {
    const version = nombre(o['version'])
    // Le jeton est plus long qu'un identifiant ordinaire: un UUID fait 36
    // caracteres, mais un jeton restaure depuis une sauvegarde peut differer.
    const token = texte(o['token'], 128)
    if (version === null || token === null || token.length === 0) return null
    return { t: 'bonjour', version: Math.round(version), token }
  }
  if (o['t'] === 'etat') {
    const e = validerEtatPresence(o['e'])
    return e ? { t: 'etat', e } : null
  }
  if (o['t'] === 'entrees') {
    const liste = o['e']
    if (!Array.isArray(liste)) return null
    // On refuse la trame avant meme de la lire quand elle est demesuree: la
    // borner apres l'avoir parcourue, c'est offrir au premier client modifie
    // venu un moyen d'occuper le serveur pour le prix d'un `send`.
    if (liste.length > MAX_ENTREES_LOT * CHAMPS_ENTREE) return null
    const nombres: number[] = []
    for (const v of liste) {
      const n = nombre(v)
      if (n === null) return null
      nombres.push(n)
    }
    // Le bornage champ par champ appartient a `decoderEntrees`: une seule
    // regle, celle que le serveur applique vraiment avant de rejouer.
    return decoderEntrees(nombres) === null ? null : { t: 'entrees', e: nombres }
  }
  if (o['t'] === 'dormir') return { t: 'dormir' }
  // Sans parametre, comme `dormir`: la demande de liste n'a rien a mentir.
  if (o['t'] === 'qui') return { t: 'qui' }
  if (o['t'] === 'dire') {
    // Le texte ressort NETTOYE et borne. Un `nom` ou un `compte` glisses a cote
    // ne sont pas refuses, ils sont simplement perdus: le message de sortie n'a
    // pas de champ ou les mettre, et c'est ce qui rend l'usurpation impossible.
    const t = nettoyerTexteChat(o['texte'])
    return t === null ? null : { t: 'dire', texte: t }
  }
  return null
}

/** Une ligne de conversation recue du serveur, ou null si elle est abimee. */
function validerMessageChat(v: unknown): MessageChat | null {
  const o = objet(v)
  if (!o) return null
  const n = nombre(o['n'])
  const nom = texte(o['nom'], 48)
  const compte = texte(o['compte'], 48)
  const t = nettoyerTexteChat(o['texte'])
  if (n === null || nom === null || compte === null || t === null) return null
  return { n: Math.round(n), nom, compte, texte: t }
}

/**
 * Longueur maximale d'une trame de faune, en entiers.
 *
 * Le client ne croit pas davantage le serveur qu'un serveur ne croit un client:
 * une trame demesuree serait une facon d'occuper la memoire du navigateur. Le
 * plafond est celui de l'emetteur, avec de la marge.
 */
const MAX_TRAME_FAUNE = MAX_FAUNE * CHAMPS_FAUNE * 2

/** Decode ce que le SERVEUR envoie. */
export function decoderServeur(brut: string): MessageServeur | null {
  let v: unknown
  try {
    v = JSON.parse(brut)
  } catch {
    return null
  }
  const o = objet(v)
  if (!o) return null
  switch (o['t']) {
    case 'bienvenue': {
      const id = texte(o['id'])
      const liste = o['joueurs']
      if (id === null || !Array.isArray(liste)) return null
      const joueurs: JoueurConnecte[] = []
      // Un compagnon illisible est ECARTE, il ne fait pas tomber l'accueil:
      // sinon un seul joueur abime empecherait de voir tous les autres.
      for (const j of liste) {
        const ok = validerJoueurConnecte(j)
        if (ok) joueurs.push(ok)
      }
      // Une heure absente ou absurde vaut zero: le client garde alors la sienne
      // plutot que de se retrouver au jour -3 000.
      const t = nombre(o['tempsJeu'])
      return { t: 'bienvenue', id, joueurs, tempsJeu: t !== null && t >= 0 ? t : 0 }
    }
    case 'horloge': {
      const t = nombre(o['tempsJeu'])
      return t === null || t < 0 ? null : { t: 'horloge', tempsJeu: t }
    }
    case 'recalage': {
      const n = nombre(o['n'])
      const x = coordonnee(o['x'])
      const z = coordonnee(o['z'])
      const rot = nombre(o['rot'])
      if (n === null || x === null || z === null || rot === null) return null
      return { t: 'recalage', n: Math.floor(n), x, z, rot }
    }
    case 'volant': {
      const id = texte(o['id'])
      // La raison est AFFICHEE telle quelle: on la borne comme une ligne de
      // chat, et pour la meme raison — elle arrive par le fil.
      const raison = texte(o['raison'], MAX_TEXTE_CHAT)
      if (id === null || raison === null) return null
      return { t: 'volant', id, raison }
    }
    case 'faune': {
      const liste = o['a']
      if (!Array.isArray(liste) || liste.length > MAX_TRAME_FAUNE) return null
      const a: number[] = []
      for (const v of liste) {
        const n = nombre(v)
        // Une trame ou UN entier manque est jetee en entier: recoller les
        // suivants decalerait tous les champs et poserait des animaux au hasard.
        if (n === null) return null
        a.push(n)
      }
      return a.length % CHAMPS_FAUNE === 0 ? { t: 'faune', a } : null
    }
    case 'refus': {
      const raison = texte(o['raison'], 200)
      return raison === null ? null : { t: 'refus', raison }
    }
    case 'arrive': {
      const j = validerJoueurConnecte(o['joueur'])
      return j ? { t: 'arrive', joueur: j } : null
    }
    case 'part': {
      const id = texte(o['id'])
      return id === null ? null : { t: 'part', id }
    }
    case 'presence': {
      const liste = o['joueurs']
      if (!Array.isArray(liste)) return null
      const joueurs: { id: string; e: EtatPresence }[] = []
      for (const j of liste) {
        const oj = objet(j)
        if (!oj) continue
        const id = texte(oj['id'])
        const e = validerEtatPresence(oj['e'])
        if (id !== null && e) joueurs.push({ id, e })
      }
      return { t: 'presence', joueurs }
    }
    case 'monde': {
      const ev = validerEvenement(o['ev'])
      return ev ? { t: 'monde', ev } : null
    }
    case 'chat': {
      const liste = o['messages']
      // Le plafond est celui de l'emetteur, avec de la marge: le client ne croit
      // pas davantage le serveur qu'un serveur ne croit un client.
      if (!Array.isArray(liste) || liste.length > HISTORIQUE_CHAT * 2) return null
      const messages: MessageChat[] = []
      // Une ligne illisible est ECARTEE, elle n'emporte pas les autres: sinon un
      // seul message abime effacerait tout l'historique du nouvel arrivant.
      for (const v of liste) {
        const m = validerMessageChat(v)
        if (m) messages.push(m)
      }
      return messages.length === 0 ? null : { t: 'chat', messages }
    }
    default:
      return null
  }
}
